import { Section } from "./section";
import { TD, TH } from "./table";

import { AvServiceType, ClassMonitoringInfoType } from "shared/types";
import {
  buildDatadogFacetedUrl,
  getClassDurationInMinutes,
  getClassStatus,
  getRecordingState,
  isFutureClass,
  nAttendees,
  roundTo2Decimals,
} from "shared/utils/monitoring";
import { formatDateTime } from "utils/date";
import { getQueryParam } from "utils/fetch";
import { getClassDetailURL, getClassRecordingURL, getObserverClassURL } from "utils/monitoring";
import { longDisplayName } from "utils/user";

function StatsHeader() {
  return (
    <thead>
      <tr>
        <TH>ID</TH>
        <TH>Org</TH>
        <TH>Course</TH>
        <TH>Class Start</TH>
        <TH>Class Duration</TH>
        <TH>Status</TH>
        <TH>Teacher</TH>
        <TH>Attendance</TH>
        <TH>In Breakout</TH>
        <TH>Bug Reports</TH>
        <TH title="Number of page loads (enter-class MonitoringEvent) per participant per hour">
          Page Loads
        </TH>
        <TH>Support Actions</TH>
        <TH>Modality</TH>
        <TH>Recording</TH>
        <TH>Pubsub Server</TH>
        <TH>Monitoring Server</TH>
        <TH>AV Service</TH>
        <TH>Licode Server</TH>
        <TH>Links</TH>
      </tr>
    </thead>
  );
}

function StatsBody({ classes }: { classes: ClassMonitoringInfoType[] }) {
  if (!classes.length) {
    return (
      <tbody>
        <tr>
          <td className="text-center h5">No active classes for given date</td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {classes.map((klass) => {
        return <ClassRow key={klass.id} klass={klass} />;
      })}
    </tbody>
  );
}

function ClassRow({ klass }: { klass: ClassMonitoringInfoType }) {
  const {
    activeBreakoutSessionId,
    avService,
    licodeServer,
    nBugReports,
    nonObserverCount,
    pageLoadStats,
    currentlyPresentUserCount,
    oncePresentUserCount,
    recordingSessionId,
    recordingState,
    supportActionStats,
    teachers,
    ...classInfo
  } = klass;
  const classStatus = getClassStatus(classInfo, currentlyPresentUserCount);
  const { title, state, danger } = getRecordingState(classInfo, recordingSessionId, recordingState);
  const futureClass = isFutureClass(classInfo);
  return (
    <tr>
      <TD>
        <a
          target="_blank"
          rel="noreferrer"
          href={`/monitoring/classes/${classInfo.id}`}
          data-testid={`class-monitoring-link-${classInfo.id}`}
        >
          {classInfo.id}
        </a>
      </TD>
      <TD title={classInfo.org.name}>{classInfo.org.short_name}</TD>
      <TD>{classInfo.course.course_code}</TD>
      <TD noWrap>{formatDateTime(classInfo.date)}</TD>
      <TD>{`${getClassDurationInMinutes(classInfo)}`}</TD>
      <TD conditionForInfo={["Active", "In Progress", "Starting soon"].includes(classStatus)}>
        {classStatus}
      </TD>
      <TD>{teachers.length ? longDisplayName(teachers[0]) : ""}</TD>
      <TD>{`${nAttendees(
        classInfo,
        currentlyPresentUserCount,
        oncePresentUserCount,
      )}/${nonObserverCount}`}</TD>
      <TD>{activeBreakoutSessionId ? "Yes" : "No"}</TD>
      <TD conditionForDanger={nBugReports >= 1}>{`${nBugReports}`}</TD>
      {!futureClass && pageLoadStats.mean ? (
        <TD
          conditionForDanger={pageLoadStats.mean >= 2}
          conditionForWarning={pageLoadStats.mean >= 1.2}
        >
          {`${roundTo2Decimals(pageLoadStats.mean) || pageLoadStats.formattedMean}`}
        </TD>
      ) : (
        <TD>-</TD>
      )}
      {!futureClass && supportActionStats.mean ? (
        <TD
          conditionForDanger={
            supportActionStats.mean >= 0.5 ||
            (!!supportActionStats.percentAffectedUsers &&
              supportActionStats.percentAffectedUsers >= 20 &&
              supportActionStats.nAffectedUsers > 3)
          }
          conditionForWarning={
            supportActionStats.mean >= 0.25 ||
            (!!supportActionStats.percentAffectedUsers &&
              supportActionStats.percentAffectedUsers >= 10)
          }
        >
          {supportActionStats.formattedMean}
        </TD>
      ) : (
        <TD>-</TD>
      )}
      <TD>{classInfo.type}</TD>
      <TD title={title} conditionForDanger={danger}>
        {state}
      </TD>
      <TD>{`${classInfo.flight_server} (${classInfo.section.flight_group})`}</TD>
      <TD conditionForDanger={!classInfo.flight_monitoring_server}>
        {classInfo.flight_monitoring_server
          ? `${classInfo.flight_monitoring_server} (${classInfo.section.flight_group})`
          : "None"}
      </TD>
      <TD>{avService || ""}</TD>
      <TD>{avService === AvServiceType.Licode ? licodeServer || "" : "N/A"}</TD>
      <TD noWrap>
        <a
          className="px1 large"
          target="_blank"
          rel="noreferrer"
          href={getObserverClassURL(classInfo)}
        >
          👀
        </a>
        <a
          className="px1 large"
          target="_blank"
          rel="noreferrer"
          href={getClassDetailURL(classInfo)}
        >
          ✏️
        </a>
        <a
          className="px1 large"
          target="_blank"
          rel="noreferrer"
          href={getClassRecordingURL(classInfo)}
        >
          🎥
        </a>
        <a
          className="px1 pt1 large"
          href={buildDatadogFacetedUrl({ classId: classInfo.id })}
          target="_blank"
          rel="noreferrer"
        >
          🐕
        </a>
      </TD>
    </tr>
  );
}

function ClassSetSummary({
  title,
  classes,
  isInitiallyOpen,
}: {
  title: string;
  classes: ClassMonitoringInfoType[];
  isInitiallyOpen?: boolean;
}) {
  if (classes.length === 0) {
    return <Section title={`${title} (${classes.length})`} disabled />;
  }
  return (
    <Section
      dataTestId={title}
      title={`${title} (${classes.length})`}
      isInitiallyOpen={isInitiallyOpen}
    >
      <table className="fds-table table-hover" style={{ tableLayout: "auto" }}>
        <StatsHeader />
        <StatsBody classes={classes} />
      </table>
    </Section>
  );
}

function SummaryTable({ classes }: { classes: ClassMonitoringInfoType[] }) {
  const dateString = getQueryParam("date") as string;

  if (dateString) {
    return (
      <table className="fds-table table-hover" style={{ tableLayout: "auto" }}>
        <StatsHeader />
        <StatsBody classes={classes} />
      </table>
    );
  }
  const activeClasses: ClassMonitoringInfoType[] = [];
  const inProgressClasses: ClassMonitoringInfoType[] = [];
  const pastClasses: ClassMonitoringInfoType[] = [];
  const futureClasses: ClassMonitoringInfoType[] = [];
  classes.forEach((klass) => {
    const status = getClassStatus(klass, klass.currentlyPresentUserCount);
    if (status === "Completed") {
      pastClasses.push(klass);
    } else if (status === "Active") {
      activeClasses.push(klass);
    } else if (status === "In Progress") {
      inProgressClasses.push(klass);
    } else {
      futureClasses.push(klass);
    }
  });

  return (
    <div className="py-3">
      <ClassSetSummary title="Classes in progress" classes={inProgressClasses} isInitiallyOpen />
      <ClassSetSummary title="Active classes" classes={activeClasses} />
      <ClassSetSummary title="Past classes" classes={pastClasses} />
      <ClassSetSummary title="Future classes" classes={futureClasses} isInitiallyOpen />
    </div>
  );
}

export { StatsHeader, StatsBody, ClassRow, ClassSetSummary, SummaryTable };
